import { observer } from 'mobx-react';
import { FC, useCallback, useMemo, useState } from 'react';
import ConfigPicker from '../../../../components/ConfigPicker/ConfigPicker';
import KioskConfigShortCodePicker from '../../components/KioskConfigShortCodePicker/KioskConfigShortCodePicker';
import { KioskTimerStart } from '../../constants';
import configKioskStore from '../../store';
import { LOCALES, maxPushNotificationMessageLength } from '~/constants/main';
import { addShortCode } from '~/helpers';
import { localeStore } from '~/mobx/localesStore';
import { TBooleanInNumber } from '~/types/main';
import { InputWithTextInside } from '~/view/components/Controls/Inputs';
import MultiLanguageTextArea from '~/view/components/Controls/MultiLanguageTextArea/MultiLanguageTextArea';
import { IPickerOption } from '~/view/components/Controls/Pickers/Picker/types';
import Switcher from '~/view/components/Controls/Switcher/Switcer';
import Text, { TEXT } from '~/view/components/Text/Text';
import './style.scss';

const KioskConfigConfigurationTab: FC = observer((): JSX.Element => {
  const [currentMessageLang, setCurrentMessageLang] = useState<LOCALES>(localeStore.language === LOCALES.AR ? LOCALES.AR : LOCALES.EN);
  const { configuration, configurationValidation } = configKioskStore;

  const changeLanguagePicker = useCallback((value: string | number) => {
    configKioskStore.changeConfig({ timeStart: value as KioskTimerStart });
  }, []);

  const changeSmsConfirmation = useCallback((value: TBooleanInNumber) => {
    configKioskStore.changeConfig({ smsConfirmation: value });
  }, []);

  const onChangeMessage = useCallback(
    (value: string, currentLanguage: LOCALES) => {
      configKioskStore.changeConfig({ message: { ...configuration.message, [currentLanguage]: value } });
    },
    [configuration.message]
  );

  const selectShortCode = useCallback(
    (options: IPickerOption[]) => {
      const value: string = addShortCode(options, configuration.message[currentMessageLang], maxPushNotificationMessageLength);

      configKioskStore.changeConfig({ message: { ...configuration.message, [currentMessageLang]: value } });
    },
    [configuration.message, currentMessageLang]
  );

  const changeAccessCode = useCallback((value: string | undefined) => {
    configKioskStore.changeConfig({ accessCode: value });
  }, []);

  const timerStartPickerOptions = useMemo((): IPickerOption[] => {
    return [
      { value: KioskTimerStart.TEN, label: KioskTimerStart.TEN + ' ' + localeStore.t('seconds') },
      { value: KioskTimerStart.FIFTEEN, label: KioskTimerStart.FIFTEEN + ' ' + localeStore.t('seconds') },
      { value: KioskTimerStart.TWENTY, label: KioskTimerStart.TWENTY + ' ' + localeStore.t('seconds') },
    ];
  }, []);

  return (
    <div className="d-flex flex-column mt-4 gap-20">
      <ConfigPicker
        title="timer_start_when_idle"
        activeTab={configuration.timeStart}
        options={timerStartPickerOptions}
        onChangeTab={changeLanguagePicker}
        titleClassName="kiosk-config__picker-title"
      />

      <div className="d-flex flex-row align-items-center">
        <Switcher status={configuration.smsConfirmation} onChange={changeSmsConfirmation} />
        <Text type={TEXT.SWITCHER_LABEL} text="enable_sms_confirmation" />
      </div>

      <KioskConfigShortCodePicker onSelectOptions={selectShortCode} />

      <MultiLanguageTextArea
        title={localeStore.t('hint_message')}
        enText={configuration.message.en}
        arText={configuration.message.ar}
        onChangeValue={onChangeMessage}
        changeCurrentLanguageCallback={setCurrentMessageLang}
        placeholder={localeStore.t('hint_message')}
        maxLength={maxPushNotificationMessageLength}
        showLength
        className="kiosk-config__text-area"
        textAreaClassName="kiosk-config__text-area-area"
      />

      <InputWithTextInside
        type="number"
        value={configuration.accessCode}
        onChangeValue={changeAccessCode}
        textProps={{ text: 'kiosk_settings_access_code' }}
        validation={configurationValidation.accessCode}
        maxLength={4}
        disableRemoveUnnecessaryZeros
        contentClassName="kiosk-config__input-wrapper"
      />
    </div>
  );
});

export default KioskConfigConfigurationTab;
